import { createContext, useEffect, useState, useContext, ReactNode } from 'react'
import ApiClient from '../api/apiClient'
import dayjs from 'dayjs'
import db from '../utilities/firebase-firestore'
import { getUserId } from '../utilities/utils'
import {
  SeatBooking,
  ApiContextType,
  BusinessUnit,
  AndiUserProfile,
  UserBooking,
  Team,
  BookedSeat
} from '../types/andTypes'
import { LoginContext } from './login-context'

export const ApiContext = createContext({} as ApiContextType)
const apiClient = ApiClient.getInstance(db)

export interface ApiProviderProps {
  children: ReactNode
}

export const ApiProvider = ({ children }: ApiProviderProps) => {
  const { authenticated, user } = useContext(LoginContext)
  const [seatBookings, setSeatBookings] = useState<SeatBooking[]>([])
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([])
  const [userProfile, setUserProfile] = useState<AndiUserProfile>()
  const [ANDis, setANDis] = useState<AndiUserProfile[]>([])

  useEffect(() => {
    if (authenticated && user?.email) {
      const unsub1 = apiClient.subscribeToBusinessUnits(data => {
        setBusinessUnits(data as BusinessUnit[])
      })

      const userId = getUserId(user.email)
      const unsub2 = apiClient.subscribeToUserProfile(userId, profileObj => {
        setUserProfile(profileObj)
      })

      const unsub3 = apiClient.subscribeToANDis(returnedANDis => {
        setANDis(returnedANDis)
      })

      return () => {
        unsub1()
        unsub2()
        unsub3()
      }
    }
  }, [authenticated])

  useEffect(() => {
    if (userProfile?.businessUnit) {
      return apiClient.subscribeToBookings(data => {
        setSeatBookings(data as SeatBooking[])
      })
    }
  }, [userProfile])
  const getSeatBookingOnDate = (date: Date): SeatBooking | undefined => {
    return seatBookings.find(seatBooking => {
      return (
        seatBooking.dateBooked &&
        seatBooking.dateBooked.setHours(0, 0, 0, 0) === date.setHours(0, 0, 0, 0)
      )
    })
  }

  const getBusinessUnitsBookings = (businessUnitId: string): SeatBooking[] => {
    return seatBookings
      .filter(date =>
        date.seats.some(seat => {
          return seat.businessUnit === businessUnitId
        })
      )
      .map(date => {
        return {
          id: date.id,
          dateBooked: date.dateBooked,
          seats: date.seats.filter(seat => seat.businessUnit === businessUnitId)
        }
      })
  }

  const getANDis = () => {
    return ANDis
  }

  const getBusinessUnitsANDis = (businessUnitId: string) => {
    return ANDis.filter(andi => andi.businessUnit === businessUnitId)
  }

  const getAndisBySelectedAndDefaultBusinessUnit = (
    selectedBu: string,
    defaultBu: string
  ): AndiUserProfile[] =>
    ANDis.filter(andi => {
      return andi.businessUnit === selectedBu || andi.businessUnit === defaultBu
    })

  const getUserBooking = (): UserBooking[] => {
    if (!user?.email) return []
    const userId = getUserId(user.email)

    return seatBookings
      .filter(booking => booking.seats.some(seat => seat.userId === userId))
      .map(seatBooking => {
        const usersSeats = seatBooking.seats.find(seat => seat.userId === userId)
        return {
          date: dayjs(seatBooking.dateBooked.setHours(0, 0, 0, 0)).format('YYYY-MM-DD'),
          clubId: usersSeats?.businessUnit || '',
          groupId: usersSeats?.groupId,
          bookingUserId: usersSeats?.bookingUserId
        }
      })
  }

  const getMyGroupBookingMembers = (
    groupId: string | undefined,
    date: Date
  ): BookedSeat[] | undefined => {
    return getSeatBookingOnDate(date)?.seats.filter(seat => seat.groupId === groupId)
  }

  const getTeams = (selectedBu: string, defaultBu: string) => {
    const andis = getAndisBySelectedAndDefaultBusinessUnit(selectedBu, defaultBu)
    const teamsStrings: string[] = []
    andis.forEach(andi => {
      if (andi.team && !teamsStrings.includes(andi.team)) {
        teamsStrings.push(andi.team)
      }
    })
    const Teams: Team[] = teamsStrings.map(team => {
      return {
        name: team,
        id: team,
        noOfMembers: andis.filter(
          andi =>
            andi.team === team &&
            (andi.businessUnit === selectedBu || andi.businessUnit === defaultBu)
        ).length,
        businessUnit: defaultBu
      }
    })
    return Teams
  }

  return (
    <ApiContext.Provider
      value={{
        apiClient,
        getSeatBookingOnDate,
        seatBookings,
        getBusinessUnitsBookings,
        getUserBooking,
        userProfile,
        businessUnits,
        getANDis,
        getBusinessUnitsANDis,
        getTeams,
        getMyGroupBookingMembers
      }}
    >
      {children}
    </ApiContext.Provider>
  )
}
